/**
 * LotSmith — Interactive equity curve.
 *
 * Hand-rolled SVG so we don't pull in a chart library (~70 KB) for what
 * is effectively a single line + area + tooltip. Tabs switch the visible
 * window: 1D / 1W / 1M / All.
 */
import { useMemo, useState } from "react";
import type { EquityPoint } from "../data/demo.js";

const RANGES = [
  { id: "1d",  label: "1D",  hours: 24 },
  { id: "1w",  label: "1W",  hours: 24 * 7 },
  { id: "1m",  label: "1M",  hours: 24 * 30 },
  { id: "all", label: "All", hours: Infinity },
] as const;

type RangeId = (typeof RANGES)[number]["id"];

interface Props { points: EquityPoint[]; }

export function EquityCurve({ points }: Props) {
  const [range, setRange] = useState<RangeId>("1m");
  const [hover, setHover] = useState<{ i: number; x: number; y: number } | null>(null);

  const visible = useMemo(() => {
    const r = RANGES.find((x) => x.id === range)!;
    if (!Number.isFinite(r.hours)) return points;
    const cutoff = Date.now() - r.hours * 60 * 60 * 1000;
    return points.filter((p) => new Date(p.ts).getTime() >= cutoff);
  }, [points, range]);

  const width = 720;
  const height = 220;
  const padding = { top: 12, right: 8, bottom: 24, left: 8 };

  const { path, area, ys, scale, lastPoint } = useMemo(() => {
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    if (visible.length === 0) {
      return { path: "", area: "", ys: [] as number[], scale: { min: 0, max: 0 }, lastPoint: null as { x: number; y: number } | null };
    }
    const eqs = visible.map((p) => p.equity);
    const min = Math.min(...eqs);
    const max = Math.max(...eqs);
    const stepX = innerW / Math.max(visible.length - 1, 1);
    const toY = (v: number) =>
      padding.top + (1 - (v - min) / Math.max(max - min, 1)) * innerH;

    const pts = visible.map((p, i) => ({
      x: padding.left + i * stepX,
      y: toY(p.equity),
    }));
    const path = pts
      .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(" ");
    const area = `${path} L${pts[pts.length - 1]!.x.toFixed(1)} ${(height - padding.bottom).toFixed(1)} L${pts[0]!.x.toFixed(1)} ${(height - padding.bottom).toFixed(1)} Z`;
    return {
      path,
      area,
      ys: pts.map((p) => p.y),
      scale: { min, max },
      lastPoint: pts[pts.length - 1] ?? null,
    };
  }, [visible]);

  const trend = visible.length >= 2 && visible[visible.length - 1]!.equity >= visible[0]!.equity ? "profit" : "loss";

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const xRel = ((e.clientX - rect.left) / rect.width) * width;
    const stepX = (width - padding.left - padding.right) / Math.max(visible.length - 1, 1);
    const i = Math.max(0, Math.min(visible.length - 1, Math.round((xRel - padding.left) / stepX)));
    setHover({ i, x: padding.left + i * stepX, y: ys[i] ?? 0 });
  }

  const hoverPoint = hover ? visible[hover.i] : null;

  return (
    <div className="eq">
      <div className="eq__hdr">
        <div className="eq__range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              className={`chip ${range === r.id ? "chip--active" : ""}`}
              onClick={() => setRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
        {hoverPoint && (
          <div className="eq__cursor num">
            <span className="eq__cursor-time">{formatTime(hoverPoint.ts)}</span>
            <span className={`eq__cursor-val tone-${trend}`}>${formatMoney(hoverPoint.equity)}</span>
          </div>
        )}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="eq__svg"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="eq-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={`var(--${trend})`} stopOpacity="0.30" />
            <stop offset="100%" stopColor={`var(--${trend})`} stopOpacity="0" />
          </linearGradient>
        </defs>
        {area && <path d={area} fill="url(#eq-grad)" />}
        {path && <path d={path} fill="none" stroke={`var(--${trend})`} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />}
        {/* End-of-line dot — pulses subtly so the trader's eye lands on the
            current value first. Hidden when hovering since the cursor dot
            takes over. */}
        {lastPoint && !hover && (
          <>
            <circle cx={lastPoint.x} cy={lastPoint.y} r="6" fill={`var(--${trend})`} opacity="0.18" />
            <circle cx={lastPoint.x} cy={lastPoint.y} r="3.5" fill={`var(--${trend})`} />
          </>
        )}
        {hover && (
          <>
            <line
              x1={hover.x} x2={hover.x}
              y1={padding.top} y2={height - padding.bottom}
              stroke="var(--border-strong)" strokeDasharray="2 3"
            />
            <circle cx={hover.x} cy={hover.y} r="4" fill={`var(--${trend})`} />
          </>
        )}
      </svg>
      <div className="eq__axis num">
        <span>${formatMoney(scale.min)}</span>
        <span>${formatMoney(scale.max)}</span>
      </div>
    </div>
  );
}

function formatMoney(v: number): string {
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
